import React, { useState } from 'react';
import { 
  Download, 
  FileText, 
  Calendar, 
  Filter,
  AlertCircle,
  CheckCircle2,
  Loader,
  Clock
} from 'lucide-react';
import api from '../services/api';

const LogsDownload = ({ station }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    timeRange: '1h',
    service: 'all',
    logLevel: 'all',
    format: 'json',
    limit: 1000
  });

  const timeRanges = [
    { value: '15m', label: '15 minutes' },
    { value: '1h', label: '1 heure' },
    { value: '6h', label: '6 heures' },
    { value: '24h', label: '24 heures' },
    { value: '7d', label: '7 jours' },
    { value: 'custom', label: 'Personnalisé' }
  ];

  const logLevels = [
    { value: 'all', label: 'Tous les niveaux' },
    { value: 'DEBUG', label: 'Debug' },
    { value: 'INFO', label: 'Info' },
    { value: 'WARNING', label: 'Warning' },
    { value: 'ERROR', label: 'Error' },
    { value: 'CRITICAL', label: 'Critical' }
  ];

  const services = [
    { value: 'all', label: 'Tous les services' },
    { value: 'iotbox', label: 'IoTBox' },
    { value: 'nginx', label: 'Nginx' },
    { value: 'worldline', label: 'Worldline' },
    { value: 'postgres', label: 'PostgreSQL' },
    { value: 'redis', label: 'Redis' },
    { value: 'telemetry-agent', label: 'Telemetry Agent' }
  ];

  const formats = [
    { value: 'json', label: 'JSON' },
    { value: 'csv', label: 'CSV' },
    { value: 'txt', label: 'Texte' }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const downloadLogs = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Appeler directement le backend pour récupérer les logs réels depuis InfluxDB
      const params = new URLSearchParams({
        time_range: filters.timeRange,
        service: filters.service,
        log_level: filters.logLevel,
        limit: filters.limit.toString(),
        format: filters.format
      });

      console.log('Téléchargement des logs depuis InfluxDB:', params.toString());

      // Récupérer les logs réels depuis le backend
      // Timeout augmenté à 40s pour permettre le traitement InfluxDB (qui peut prendre 20-35s)
      const response = await api.get(`/api/v1/stations/${station.station_id}/logs/download?${params}`, {
        responseType: filters.format === 'json' ? 'json' : 'blob',
        timeout: 40000,  // 40 secondes pour les requêtes de logs (surcharge le timeout par défaut de 10s)
        signal: undefined  // Pas d'AbortController pour cette requête longue
      });

      // Si format JSON, afficher un résumé et proposer le téléchargement
      if (filters.format === 'json' && typeof response.data === 'object') {
        const logsData = response.data;

        setResult({
          type: 'logs_downloaded',
          message: `${logsData.total_logs || 0} logs récupérés avec succès`,
          logs: logsData.logs || [],
          totalLogs: logsData.total_logs || 0,
          filters: logsData.filters,
          grafanaUrl: logsData.grafana_url,
          queryUsed: logsData.query_used
        });

        // Télécharger automatiquement le fichier JSON
        const blob = new Blob([JSON.stringify(logsData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `logs_${station.station_id}_${filters.timeRange}_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        // Format texte ou CSV - téléchargement direct
        const blob = response.data instanceof Blob ? response.data : new Blob([response.data], {
          type: filters.format === 'csv' ? 'text/csv' : 'text/plain'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `logs_${station.station_id}_${filters.timeRange}_${new Date().toISOString().split('T')[0]}.${filters.format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        setResult({
          type: 'logs_downloaded',
          message: `Logs téléchargés avec succès au format ${filters.format.toUpperCase()}`,
          totalLogs: 'unknown'
        });
      }

    } catch (error) {
      console.error('Erreur téléchargement logs:', error);
      setError({
        type: 'error',
        message: error.response?.data?.detail || error.response?.data?.error || error.message || 'Erreur lors du téléchargement des logs'
      });
    } finally {
      setLoading(false);
    }
  };


  if (!station) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center space-x-2 text-gray-600">
          <AlertCircle className="h-5 w-5" />
          <span>Station non disponible</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <span>Logs & Télémétrie</span>
          </h3>
          <div className="flex items-center space-x-2 text-sm">
            <div className="flex items-center space-x-1">
              <div className={`w-2 h-2 rounded-full ${station.status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
              <span className={`font-medium ${station.status === 'online' ? 'text-green-600' : 'text-red-600'}`}>
                Station {station.status === 'online' ? 'Online' : 'Hors ligne'}
              </span>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="space-y-4 mb-4">
          {/* Première ligne: Période, Service, Niveau */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Période */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Clock className="h-4 w-4 inline mr-1" />
                Période
              </label>
              <select
                value={filters.timeRange}
                onChange={(e) => handleFilterChange('timeRange', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {timeRanges.map(range => (
                  <option key={range.value} value={range.value}>{range.label}</option>
                ))}
              </select>
            </div>

            {/* Service */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Filter className="h-4 w-4 inline mr-1" />
                Service
              </label>
              <select
                value={filters.service}
                onChange={(e) => handleFilterChange('service', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {services.map(service => (
                  <option key={service.value} value={service.value}>{service.label}</option>
                ))}
              </select>
            </div>

            {/* Niveau de log */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Niveau
              </label>
              <select
                value={filters.logLevel}
                onChange={(e) => handleFilterChange('logLevel', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {logLevels.map(level => (
                  <option key={level.value} value={level.value}>{level.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Deuxième ligne: Format, Limite */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Format */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FileText className="h-4 w-4 inline mr-1" />
                Format d'export
              </label>
              <select
                value={filters.format}
                onChange={(e) => handleFilterChange('format', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {formats.map(format => (
                  <option key={format.value} value={format.value}>{format.label}</option>
                ))}
              </select>
            </div>

            {/* Limite */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Limite (nombre de logs)
              </label>
              <input
                type="number"
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="100"
                max="10000"
                step="100"
              />
            </div>
          </div>
        </div>

        {/* Messages de résultat/erreur */}
        {result && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <div className="flex items-start space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">{result.message}</p>
                {result.totalLogs && typeof result.totalLogs === 'number' && (
                  <p className="text-xs text-green-600 mt-1">
                    Fichier téléchargé avec {result.totalLogs} entrées de log
                  </p>
                )}
                {result.grafanaUrl && (
                  <div className="mt-2">
                    <a
                      href={result.grafanaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-green-700 hover:text-green-800 underline"
                    >
                      Voir dans Grafana →
                    </a>
                  </div>
                )}
              </div>
              <button
                onClick={() => setResult(null)}
                className="text-green-400 hover:text-green-600"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800">{error.message}</p>
              </div>
              <button 
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Bouton de téléchargement */}
        <div className="flex space-x-3">
          <button
            onClick={downloadLogs}
            disabled={loading}
            className={`flex-1 px-6 py-3 rounded-lg font-medium shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 ${
              loading 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <Loader className="h-4 w-4 animate-spin" />
                <span>Téléchargement...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-2">
                <Download className="h-4 w-4" />
                <span>Télécharger les Logs</span>
              </div>
            )}
          </button>

          <a
            href="http://telemetry.fthek.be:3000/d/a35441ae-c289-4f26-abc8-d4b700378085/iotbox-logs-and-recherche"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 flex items-center space-x-2 shadow-md hover:shadow-lg transform hover:scale-105"
          >
            <Calendar className="h-4 w-4" />
            <span>Ouvrir Grafana</span>
          </a>
        </div>

        {/* Informations sur les sources */}
        <div className="mt-4 text-xs text-gray-500">
          <p>Sources de données :</p>
          <ul className="list-disc list-inside mt-1">
            <li>Logs temps réel via agent de la station</li>
            <li>Historique InfluxDB (télémétrie centralisée)</li>
            <li>Dashboard Grafana pour visualisation avancée</li>
          </ul>
          {station.status === 'offline' && (
            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-yellow-800">
                <strong>Note :</strong> Les logs historiques sont disponibles même pour les stations hors ligne via InfluxDB.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogsDownload;



